/** 결재자/문서 상태 → 뱃지 톤/prefix 매핑 + UI 유틸 */

import { pickI18nText } from '@/utils/approval/i18nText';
import sortApproversByOrder from '@/utils/approval/sortApproversByOrder';
import type {
  ApprovalLineRowGroup,
  ApprovalViewApprover,
  ApproverStatus,
  DocumentStatus,
  Lang,
} from './types';

/** 상태 뱃지 톤 키. 실제 색/배경은 styled 정의에서 매핑. */
export type StatusBadgeTone =
  | 'neutral'
  | 'info'
  | 'warning'
  | 'danger'
  | 'success';

export type StatusBadgePrefix = 'check' | 'asterisk' | 'warning' | null;

export type StatusBadgeConfig = {
  /** 라벨 base 텍스트. 'C' 상태는 approvedAt(YYYY-MM-DD) 으로 동적 치환 */
  label: string;
  tone: StatusBadgeTone;
  prefix: StatusBadgePrefix;
};

/** 결재자 status → 뱃지 키 매핑. */
export const APPROVER_STATUS_CONFIG: Record<ApproverStatus, StatusBadgeConfig> =
  {
    '0': { label: '대기중', tone: 'neutral', prefix: null },
    P: { label: '진행중', tone: 'info', prefix: 'asterisk' },
    H: { label: '보류', tone: 'warning', prefix: null },
    R: { label: '반려', tone: 'danger', prefix: 'warning' },
    C: { label: '완료', tone: 'success', prefix: 'check' },
    X: { label: '취소', tone: 'danger', prefix: 'warning' },
  };

/** 문서 status → 상태별 뱃지 컬러 매핑 */
export const DOCUMENT_STATUS_CONFIG: Record<DocumentStatus, StatusBadgeConfig> =
  {
    S: { label: '임시저장', tone: 'neutral', prefix: null },
    P: { label: '진행중', tone: 'info', prefix: 'asterisk' },
    H: { label: '보류', tone: 'warning', prefix: null },
    C: { label: '완료', tone: 'success', prefix: 'check' },
    D: { label: '삭제', tone: 'neutral', prefix: null },
    R: { label: '반려', tone: 'danger', prefix: 'warning' },
    X: { label: '취소', tone: 'danger', prefix: 'warning' },
  };

/**
 * 카드 chip 날짜 — YYYY-MM-DD. 값 없음/파싱 불가는 null.
 * 폴백 라벨은 caller 가 t() 로 결정할 것 — 날짜 문자열을 t() 에 넘기면 매번 미해결 키 조회가 된다.
 */
export const formatCardDate = (
  iso: string | null | undefined,
): string | null => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

/** 결재자 의견 타임스탬프 — YYYY-MM-DD HH:mm:ss (로컬 타임존). 빈 입력은 빈 문자열. */
export const formatOpinionAt = (iso: string | null): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(
    d.getHours(),
  )}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
};

// 회수·반려요청 사유는 사용자 입력 → HTML 삽입 전 escape (고정문구는 t() 신뢰).
const escapeHtml = (s: string) =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/**
 * v2 회수 사유를 반려 의견 HTML 로 조립 — 코멘트 로그 행과 의견 박스가 동일 문구를 쓰는 단일 출처.
 */
export const buildWithdrawalRejectionHtml = (
  reason: string | null,
  t: (key: string) => string,
): string => {
  const safe = reason ? escapeHtml(reason) : '';
  return `${t(
    '[품의 회수에 의한 반려] 작성자의 회수 요청으로 인해 반려 처리 되었습니다.',
  )}<br>${t('회수 사유')} : ${safe}`;
};

/**
 * 기안자의 반려요청 사유를 안내 HTML 로 조립 — 의견 박스와 코멘트 로그 행의 단일 출처.
 *
 * 회수(buildWithdrawalRejectionHtml)와 달리 문서 상태가 바뀌지 않으므로 반려 처리 문구를 쓰지 않는다.
 * 요청자는 항상 기안자로 고정이고 요청 시각은 응답·로그 컬럼에 없어(PORTAL-14138) 문구에 담지 않는다.
 */
export const buildReturnRequestHtml = (
  reason: string | null,
  t: (key: string) => string,
): string => {
  const safe = reason ? escapeHtml(reason) : '';
  return `${t('[반려요청] 작성자가 반려를 요청했습니다.')}<br>${t(
    '반려요청 사유',
  )} : ${safe}`;
};

/** 공유 유틸 재노출 — 기존 consumer 의 `./statusMapping` import 경로 유지용 alias. */
export const pickLang = pickI18nText;

/** i18n.language(예: 'en-US') → I18nText 키('ko'|'en'|'zh'). 미매칭은 ko. */
export const normalizeLang = (i18nLang: string | undefined): Lang => {
  if (!i18nLang) return 'ko';
  const lower = i18nLang.toLowerCase();
  if (lower.startsWith('en')) return 'en';
  if (lower.startsWith('zh')) return 'zh';
  return 'ko';
};

/**
 * 결재자 배열에서 협조결재(type=2) 연속 구간을 cooperation 그룹으로 묶음.
 *
 * 응답 approvers[] 는 order 순이 아니라 intranetId 알파벳 순으로 내려오므로
 * 묶기 전에 order 로 정렬한다 — 정렬 없이 묶으면 같은 협조 묶음이 여러 그룹으로 쪼개지고
 * 그룹 순서도 역전된다. 반환 그룹과 그룹 내부 멤버 모두 order 오름차순이 보장된다.
 */
export const groupApprovers = (
  approvers: ApprovalViewApprover[],
): ApprovalLineRowGroup[] => {
  const result: ApprovalLineRowGroup[] = [];
  let buffer: ApprovalViewApprover[] = [];

  const flushCoop = () => {
    if (buffer.length > 0) {
      result.push({ kind: 'cooperation', members: buffer });
      buffer = [];
    }
  };

  for (const a of sortApproversByOrder(approvers)) {
    if (a.type === 2) {
      buffer.push(a);
    } else {
      flushCoop();
      result.push({ kind: 'single', approver: a });
    }
  }
  flushCoop();
  return result;
};
